import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { getErrorMessage } from '@billda/shared-ui';
import { LabourGroupSelect } from '../../labour/components/LabourGroupSelect';
import {
  useAssignLabourGroup,
  useRemoveLabourGroup,
} from '../hooks/useLabourGroupAssignment';
import { resolveLabourGroupSaveIntent } from '../utils/labourGroupSaveIntent';

interface UserLabourGroupAssignmentProps {
  userId: string;
  currentGroupId?: number | null;
  currentGroupName?: string | null;
  onUpdate?: () => void;
}

export const UserLabourGroupAssignment: React.FC<
  UserLabourGroupAssignmentProps
> = ({ userId, currentGroupId, currentGroupName, onUpdate }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(
    currentGroupId ?? null
  );

  const assignMutation = useAssignLabourGroup();

  const removeMutation = useRemoveLabourGroup();

  const isLoading = assignMutation.isPending || removeMutation.isPending;
  const error = assignMutation.error ?? removeMutation.error;
  const intent = resolveLabourGroupSaveIntent(selectedGroupId, currentGroupId);

  const resetMutations = () => {
    assignMutation.reset();
    removeMutation.reset();
  };

  const handleEdit = () => {
    setIsEditMode(true);
    setSelectedGroupId(currentGroupId ?? null);
    resetMutations();
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setSelectedGroupId(currentGroupId ?? null);
    resetMutations();
  };

  const handleSave = () => {
    if (intent.action === 'none') {
      setIsEditMode(false);
      return;
    }

    const onSuccess = () => {
      setIsEditMode(false);
      onUpdate?.();
    };

    if (intent.action === 'remove') {
      removeMutation.mutate(userId, { onSuccess });
      return;
    }

    assignMutation.mutate({ userId, groupId: intent.groupId }, { onSuccess });
  };

  if (isEditMode) {
    return (
      <Box sx={{ mt: 0.5, mb: 1 }} data-testid="labour-group-edit-mode">
        <Stack spacing={1}>
          {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}

          <LabourGroupSelect
            value={selectedGroupId}
            onChange={setSelectedGroupId}
            disabled={isLoading}
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={isLoading || intent.action === 'none'}
              data-testid="labour-group-save-button"
            >
              Save
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mt: 0.5,
        mb: 1,
      }}
      data-testid="labour-group-display"
    >
      <Typography
        sx={{ fontWeight: 500 }}
        variant="body1"
        data-testid="labour-group-name"
      >
        {currentGroupName || 'Not assigned'}
      </Typography>
      <IconButton
        size="small"
        onClick={handleEdit}
        data-testid="labour-group-edit-button"
        sx={{
          p: 0.5,
        }}
      >
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
